import React, { PureComponent } from 'react';
import { string, node, shape, bool, func } from 'prop-types';
import camelCase from 'lodash/camelCase';
import classnames from 'classnames';
import { Icons, CollapseTransition } from 'components';
import './CollapsePanel.scss';

/** CollapsePanel can be either controlled by its parent or manage its own state.
 * To switch it in "controlled" mode (meaning, it will be controlled by its parent)
 * you need to pass it an onChange function and opened boolean as props
 */
export default class CollapsePanel extends PureComponent {
    static propTypes = {
        className: string,
        label: string.isRequired,
        children: node,
        theme: shape({
            children: string,
        }),
        dataQa: string,
        /** Disable the toggle interaction. */
        clickToggleDisabled: bool,
        /** Open (true) or close (false) the panel when the component is controlled by its parent */
        opened: bool,
        /** Function that will be called when the user try to collapse/uncollapse the panel in controlled mode.
         * The function will receive the new value of opened as parameters
         */
        onChange: func,
        /** Function called when the user clicks anywhere in collapsePanel */
        onClick: func,
    };

    static defaultProps = {
        className: null,
        theme: {},
        dataQa: null,
        clickToggleDisabled: false,
        opened: null,
        onChange: null,
        onClick: null,
        children: null,
    };

    static getDerivedStateFromProps(props, state) {
        if (state.controlled && props.opened !== state.opened) {
            return { opened: props.opened };
        }

        return null;
    }

    constructor(props) {
        super(props);

        this.state = {
            opened: props.opened !== null ? props.opened : true,
            controlled: props.onChange && props.opened !== null,
        };
    }

    handleHeaderClick = () => {
        if (this.props.clickToggleDisabled) return;

        const { controlled, opened } = this.state;
        if (controlled) {
            this.props.onChange(!this.props.opened);
        } else {
            this.setState({ opened: !opened });
        }
    };

    render() {
        const {
            className,
            label,
            children,
            theme,
            clickToggleDisabled,
            dataQa,
            onClick,
        } = this.props;
        const { opened } = this.state;
        const rootClassnames = classnames(
            'collapse-panel-container',
            {
                'collapse-panel-container--opened': opened,
            },
            className,
        );

        const collapseClassnames = classnames(
            'collapse-panel-container__children',
            theme.children,
        );

        const buttonOptionalProps = {};
        if (dataQa) {
            buttonOptionalProps['data-qa'] = `CollapsePanel${camelCase(
                dataQa,
            )}Cta`;
        }

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
            <div className={rootClassnames} onClick={onClick}>
                {label && (
                    <button
                        className="collapse-panel-container__header"
                        data-button={`collapsePanel${label}`}
                        onClick={this.handleHeaderClick}
                        {...buttonOptionalProps}
                    >
                        <h3 className="collapse-panel-container__header__label">
                            {label}
                        </h3>

                        {!clickToggleDisabled && (
                            <Icons.ChevronLeft className="collapse-panel-container__header__chevron" />
                        )}
                    </button>
                )}

                <CollapseTransition
                    className={collapseClassnames}
                    in={opened}
                    childrenClassName="collapse-panel-container__children__wrapper"
                >
                    {children}
                </CollapseTransition>
            </div>
        );
    }
}
